import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';

import { Grid, Notification } from '@xsolla/ui-core';
import { useModal } from '@xsolla/ui-modal';

import { LoginProjectConfiguration } from '../../api';
import {
  AuthenticationMethodSettings,
  AuthenticationSettings,
  CustomizationSettings,
  CustomProjectName,
  DataBaseSettings,
  DeleteLoginForm,
  LegalSettings,
  ProvidersSettings,
  SecureSettings,
  SuccessDeleteLoginForm
} from '../../components';
import { Breadcrumbs, DeleteButton, PageHeader } from '../../components/ui';
import { AlertIcon, CopyIcon } from '../../components/ui/icons';
import { SHADOW_PROJECT_TYPE } from '../../constants';
import { useProjects } from '../../contexts/settings';
import { usePaths } from '../../routes/routesHelper';
import { XsollaAnalytics } from '../../types/publisher';
import { useFirstRender } from '../../utils';
import { DeleteButtonWrapper, ProjectIDWrapper, Title } from './style';

const MainNotificationWrapper = styled.div`
  margin-bottom: 24px;
`;

type Props = {
  isAgreementSigned: boolean;
  currentProject: LoginProjectConfiguration | null;
  merchantId: number;
  xsollaAnalytics: XsollaAnalytics;
  projectId: string;
  history: RouteComponentProps['history'];
};

const NavigationSection = ({
  history,
  isAgreementSigned,
  currentProject,
  merchantId,
  xsollaAnalytics,
  projectId
}: Props): JSX.Element => {
  const intl = useIntl();
  const { deleteProject } = useProjects();
  const firstRender = useFirstRender();
  const { dashboardPath } = usePaths();
  const [deleteFetching, setDeleteFetching] = useState<boolean>(false);
  const [errorDelete, setErrorDelete] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const idLoginProject = currentProject?.id ?? '';
  const nameLoginProject = currentProject?.name ?? '';
  const isShadowProject = currentProject?.type === SHADOW_PROJECT_TYPE;

  useEffect(() => {
    if (!firstRender && copied) {
      xsollaAnalytics.sendClick('login_pa_module__navigation__copy_project_id', projectId);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  const [onOpenModal, onCloseModal] = useModal(
    () => (
      <DeleteLoginForm
        onClose={onCloseModal}
        deleteFetching={deleteFetching}
        projectName={nameLoginProject}
        onDelete={deleteLogin}
        projectId={currentProject?.id}
        merchantId={merchantId}
        errorDelete={errorDelete}
      />
    ),
    [currentProject, deleteFetching, errorDelete]
  );

  const [onOpenSuccessModal, onCloseSuccessModal] = useModal(
    () => <SuccessDeleteLoginForm onClose={handleCloseSuccessDeleteModal} />,
    [currentProject, deleteFetching]
  );

  const handleCloseSuccessDeleteModal = useCallback(() => {
    onCloseSuccessModal();
    history.push({ pathname: dashboardPath() });
  }, [currentProject, deleteFetching]);

  const deleteLogin = useCallback(async () => {
    setDeleteFetching(true);

    try {
      await deleteProject(idLoginProject);
      onOpenSuccessModal();
      onCloseModal();
    } catch {
      setErrorDelete(true);
    } finally {
      setDeleteFetching(false);
    }
  }, [currentProject, deleteFetching]);

  const breadcrumbItems = [
    {
      label: intl.messages['dashboard.label'],
      to: dashboardPath()
    },
    {
      label: <CustomProjectName />,
      to: '#'
    }
  ];

  const projectIdButton = (
    <Title>
      <CopyToClipboard text={projectId}>
        <ProjectIDWrapper disabled={copied} onClick={() => setCopied(true)}>
          {copied
            ? intl.messages['dashboard.login-card.copied']
            : intl.messages['dashboard.login-card.copy']}
          <CopyIcon disabled={copied} />
        </ProjectIDWrapper>
      </CopyToClipboard>
    </Title>
  );

  return (
    <>
      <PageHeader
        title={<Breadcrumbs items={breadcrumbItems} />}
        projectIdButton={projectIdButton}
      />
      <MainNotificationWrapper>
        <Notification icon={AlertIcon}>{intl.messages['navigation.main-alert']}</Notification>
      </MainNotificationWrapper>
      <Grid cols={1} gap={24}>
        <AuthenticationMethodSettings />
        <SecureSettings />
        {(!isShadowProject && <AuthenticationSettings />) as ReactElement}
        {(!isShadowProject && <DataBaseSettings />) as ReactElement}
        <ProvidersSettings />
        <CustomizationSettings isAgreementSigned={isAgreementSigned} />
        <LegalSettings />
      </Grid>
      <DeleteButtonWrapper>
        <DeleteButton onClick={onOpenModal} />
      </DeleteButtonWrapper>
    </>
  );
};

export default NavigationSection;
